/**
 * 实体选择模式。
 *
 * 设计说明：
 * 1. 该模式只描述“当前命令允许用户优先选择哪类实体”；
 * 2. 命令宿主只通过 `Viewer -> EntitySelectionManager` 设置模式，不直接介入拾取实现；
 * 3. 统一在 cad-core 建模，避免 web-client 各命令再私下拼一套字符串协议。
 */
export type EntitySelectionMode =
  | 'object'
  | 'face'
  | 'edge'
  | 'vertex'
  | 'face-edge'
  | 'topology';

/**
 * 实体选择模式切换选项。
 *
 * 设计说明：
 * 1. 选择模式描述实体类型，本选项补充命令会话所需的选择范围；
 * 2. 外部草图能力默认关闭，避免普通草图编辑误选其它草图；
 * 3. 调用方只声明选择意图，具体候选过滤仍由 cad-core 统一完成。
 */
export interface EntitySelectionModeOptions {
  /** 切换选择上下文时是否保留仍合法的已有选择。 */
  preserveSelection?: boolean;
  /** 是否允许当前活动草图之外的草图实体参与候选收集。 */
  allowExternalSketchEntities?: boolean;
}

/**
 * 归一化外部传入的选择模式。
 *
 * @param mode 外部模式值。
 * @returns 合法模式；非法值一律回退到 `object`。
 */
export function normalizeEntitySelectionMode(mode: unknown): EntitySelectionMode {
  switch (mode) {
    case 'face':
    case 'edge':
    case 'vertex':
    case 'face-edge':
    case 'topology':
      return mode;
    default:
      return 'object';
  }
}

/**
 * 判断当前模式是否允许优先选择边。
 *
 * @param mode 选择模式。
 * @returns 是否允许边。
 */
export function isEdgeAwareSelectionMode(mode: EntitySelectionMode): boolean {
  return mode === 'edge' || mode === 'face-edge' || mode === 'topology';
}

/**
 * 判断当前模式是否允许选择面。
 *
 * @param mode 选择模式。
 * @returns 是否允许面。
 */
export function isFaceAwareSelectionMode(mode: EntitySelectionMode): boolean {
  return mode === 'face' || mode === 'face-edge' || mode === 'topology';
}

/**
 * 判断当前模式是否允许选择顶点。
 *
 * @param mode 选择模式。
 * @returns 是否允许顶点。
 */
export function isVertexAwareSelectionMode(mode: EntitySelectionMode): boolean {
  return mode === 'vertex' || mode === 'topology';
}
